import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import Typography from '../../components/Typography';
import Button from '../../components/Button/Button';
import Modal from '../../components/Modal/Modal';
import CardToogle from '../Toogle/CardToogle';
import Chip from '../Chip';
import RevertIcon from '../Icons/RevertIcon'
import { getEventTypeSvgIcon } from '../../utilities';
import { EVENT_TYPES, EXPERIENCE_LEVELS, TERRAIN_TYPES, TICKET_TYPES } from '../../constants/eventTypes';
import { ALL_VEHICLE_TYPES, getVehicleTypeLabel, sortVehicleTypesByPriority } from '../../constants/vehicleTypes';

const ALL_VALUES = {
  typology: EVENT_TYPES,
  terrain: TERRAIN_TYPES,
  experience: EXPERIENCE_LEVELS,
  ticketType: TICKET_TYPES,
  needsVehicle: [true, false],
  vehicleTypes: sortVehicleTypesByPriority(ALL_VEHICLE_TYPES)
};

const FiltersModal = ({
  tempOtherFilters,
  handleOtherFiltersChange,
  handleCancelOtherFilters,
  handleApplyOtherFilters,
  handleCloseOtherFiltersModal,
  handleResetFilters,
  categoryKeys,
  tFilter,
  tCategories
}) => {

  // Comprueba si todas las opciones para un filtro están seleccionadas
  const isAllActive = (filterType) => {
    const allValues = ALL_VALUES[filterType] || [];
    return allValues.every(val => tempOtherFilters[filterType].includes(val));
  };

  // Función para manejar el toggle de "all" (por filtro)
  const handleToggleAll = (filterName) => {
    const allValues = ALL_VALUES[filterName] || [];
    if (isAllActive(filterName)) {
      // Si ya están todas, se deselecciona todo
      handleOtherFiltersChange(filterName, [], true);
    } else {
      // Si no están todas, se asigna el array completo
      handleOtherFiltersChange(filterName, allValues, true);
    }
  };


  return (
    <ModalWrapper>
      <Modal
        title={tFilter('modalFilters.title')}
        onClose={handleCloseOtherFiltersModal}
        maxWidth="500px"
      >
        <ModalContent>
          {/* Categorías de eventos */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.categoryFilterLabel')}
            </Typography>
            <FilterGridBlock>
              {categoryKeys.map((category) => (
                <CardToogle
                  key={category}
                  category={tCategories(`type.${category}`)}
                  isActive={tempOtherFilters.typology.includes(category)}
                  onClick={() => handleOtherFiltersChange('typology', category)}
                  icon={getEventTypeSvgIcon(
                    category,
                    tempOtherFilters.typology.includes(category) ? theme.colors.brandMain : theme.colors.defaultSubtle
                  )}
                  iconSize="24px"
                  direction="horizontal"
                />
              ))}
            </FilterGridBlock>
          </FilterSection>

          {/* Filtro de Ticket */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.ticketFilterLabel')}
            </Typography>
            <FilterListBlock>
              {ALL_VALUES.ticketType.map(type => (
                <Chip
                  key={type}
                  category={tFilter(`ticketFilters.ticket.${type}`)}
                  isActive={tempOtherFilters.ticketType.includes(type)}
                  onClick={() => handleOtherFiltersChange('ticketType', type)}
                />
              ))}
              <Chip
                key="all-ticket"
                category={tFilter(`ticketFilters.ticket.all`)}
                isActive={isAllActive('ticketType')}
                onClick={() => handleToggleAll('ticketType')}
              />
            </FilterListBlock>
          </FilterSection>

          {/* Filtro de Necesidad de Vehículo */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.vehicleFilterLabel')}
            </Typography>
            <FilterListBlock>
              {ALL_VALUES.needsVehicle.map(value => (
                <Chip
                  key={value}
                  category={tFilter(`vehicleFilters.${value === 'true' ? 'yes' : 'no'}`)}
                  isActive={tempOtherFilters.needsVehicle.includes(value)}
                  onClick={() => handleOtherFiltersChange('needsVehicle', value)}
                />
              ))}
              <Chip
                key="all-needsVehicle"
                category={tFilter(`vehicleFilters.all`)}
                isActive={isAllActive('needsVehicle')}
                onClick={() => handleToggleAll('needsVehicle')}
              />
            </FilterListBlock>
          </FilterSection>

          {/* Filtro de Terreno */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.terrainFilterLabel')}
            </Typography>
            <FilterListBlock>
              {ALL_VALUES.terrain.map(option => (
                <Chip
                  key={option}
                  category={tFilter(`terrainFilters.terrain.${option}`)}
                  isActive={tempOtherFilters.terrain.includes(option)}
                  onClick={() => handleOtherFiltersChange('terrain', option)}
                />
              ))}
              <Chip
                key="all-terrain"
                category={tFilter(`terrainFilters.terrain.all`)}
                isActive={isAllActive('terrain')}
                onClick={() => handleToggleAll('terrain')}
              />
            </FilterListBlock>
          </FilterSection>

          {/* Filtro de Tipos de Vehículos */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.vehicleTypesFilterLabel')}
            </Typography>
            <FilterListBlock>
              {ALL_VALUES.vehicleTypes.map(type => (
                <Chip
                  key={type}
                  category={getVehicleTypeLabel(type)}
                  isActive={tempOtherFilters.vehicleTypes?.includes(type)}
                  onClick={() => handleOtherFiltersChange('vehicleTypes', type)}
                />
              ))}
              <Chip
                key="all-vehicleTypes"
                category={tFilter(`vehicleTypeFilters.vehicleTypes.all`)}
                isActive={isAllActive('vehicleTypes')}
                onClick={() => handleToggleAll('vehicleTypes')}
              />
            </FilterListBlock>
          </FilterSection>

          {/* Filtro de Experiencia */}
          <FilterSection>
            <Typography as="label" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
              {tFilter('modalFilters.experienceFilterLabel')}
            </Typography>
            <FilterListBlock>
              {ALL_VALUES.experience.map(level => (
                <Chip
                  key={level}
                  category={tFilter(`experienceFilters.experience.${level}`)}
                  isActive={tempOtherFilters.experience.includes(level)}
                  onClick={() => handleOtherFiltersChange('experience', level)}
                />
              ))}
              <Chip
                key="all-experience"
                category={tFilter(`experienceFilters.experience.all`)}
                isActive={isAllActive('experience')}
                onClick={() => handleToggleAll('experience')}
              />
            </FilterListBlock>
          </FilterSection>

          <Button type="button" $variant="ghost" onClick={handleResetFilters} size="l" sizeTablet="l" sizeMobile="m">
            <RevertIcon fill={theme.colors.defaultWeak} height={20} width={20} />
            {tFilter('buttons.reset')}
          </Button>
        </ModalContent>

        {/* Action Buttons */}
        <ModalActions>
          <ActionButtonsGroup>
            <ModalAction $variant="outline" onClick={handleCancelOtherFilters} size="l" sizeTablet="l" sizeMobile="m">
              {tFilter('modalLocationTime.cancel')}
            </ModalAction>
            <ModalAction onClick={handleApplyOtherFilters} size="l" sizeTablet="l" sizeMobile="m">
              {tFilter('modalLocationTime.apply')}
            </ModalAction>
          </ActionButtonsGroup>
        </ModalActions>
      </Modal>
    </ModalWrapper>
  );
};

export default FiltersModal;

// Estilos
const ModalWrapper = styled.div`
  align-items: center;
  background-color: rgb(0 0 0 / 50%);
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1000;
`;

const ModalContent = styled.div`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: ${({ theme }) => theme.radius.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  width: 100%;
`;

const FilterGridBlock = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.xs};
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FilterListBlock = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${({ theme }) => theme.sizing.xs};
  max-width: 100%;
`;

const ModalActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  max-width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const ActionButtonsGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const ModalAction = styled(Button)`
  flex-grow: 1;
`;